import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Search, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { SearchResults, countItems } from './SearchResults';
import { useSiteSearch, keyNav } from './useSiteSearch';
import { hasResults } from '../../lib/siteSearch';

export const SiteSearchDialog = ({ open, onOpenChange }) => {
  const s = useSiteSearch();
  const navigate = useNavigate();
  const box = useRef(null);
  const close = () => onOpenChange(false);
  useEffect(() => { if (!open) s.setQ(''); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const onKey = (e) => {
    if (keyNav(s, countItems(s.r, true), box)(e)) return;
    if (e.key === 'Enter' && s.q.trim().length >= 2) { close(); navigate(`/search?q=${encodeURIComponent(s.q.trim())}`); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 max-w-2xl top-[10%] translate-y-0 sm:rounded-2xl overflow-hidden [&>button]:hidden" data-testid="site-search-dialog">
        <DialogTitle className="sr-only">Search Apollo Shades</DialogTitle>
        <div className="flex items-center gap-3 px-4 border-b border-neutral-200">
          <Search className="w-5 h-5 text-neutral-400 shrink-0" />
          <input autoFocus value={s.q} onChange={(e) => s.setQ(e.target.value)} onKeyDown={onKey} role="combobox" aria-expanded={!!s.r} aria-label="Search the site"
            placeholder="Search products, fabrics, colors, guides…" className="flex-1 py-4 text-base bg-transparent border-0 outline-none focus:outline-none focus:ring-0 focus-visible:outline-none" data-testid="site-search-input" />
          {s.loading && <Loader2 className="w-4 h-4 animate-spin text-neutral-400" />}
          <button type="button" onClick={close} className="p-1.5 rounded-full hover:bg-neutral-100 text-neutral-500" aria-label="Close search" data-testid="site-search-close"><X className="w-4 h-4" /></button>
        </div>
        <div ref={box} className="max-h-[65vh] overflow-y-auto p-2" data-testid="site-search-results">
          {s.r && hasResults(s.r) && <SearchResults r={s.r} active={s.active} onPick={close} compact />}
          {s.r && !hasResults(s.r) && <p className="px-3 py-8 text-center text-sm text-neutral-500" data-testid="site-search-empty">No results for “{s.q}”. Try a product, color or collection name.</p>}
          {!s.r && <p className="px-3 py-6 text-sm text-neutral-400">Try “motorized roller shades”, “blackout”, “gray”, “Basic BO” or “warranty”.</p>}
        </div>
        {s.r && hasResults(s.r) && (
          <button type="button" onClick={() => { close(); navigate(`/search?q=${encodeURIComponent(s.q.trim())}`); }} className="w-full text-left px-5 py-3 text-xs text-neutral-600 border-t border-neutral-200 hover:bg-neutral-50" data-testid="site-search-see-all">
            See all results for “{s.q}” <span className="text-neutral-400">· ↑↓ to move, Enter to open</span>
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
};
